"use client";

import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {cn} from "@/lib/utils";
import {type CodeExecutionStepDetail} from "../toolCallUtils";
import StepCodeBlock from "./StepCodeBlock";
import StepOutput from "./StepOutput";

const CODE_LINE_CAP = 6;
const OUTPUT_LINE_CAP = 3;

/** Lines with stable keys so capped lists avoid array-index keys. */
const keyedLines = (value: string) =>
   value.split("\n").map((line, idx) => ({
      key: `${idx}:${line.length}:${line.slice(0, 12)}`,
      line,
   }));

type StepDetailContentProps = Readonly<{
   detail: CodeExecutionStepDetail;
   /** A single-step group renders content directly under the group trigger. */
   contentOnly: boolean;
   showAllCode: boolean;
   onToggleAllCode: () => void;
   showAllOutput: boolean;
   onToggleAllOutput: () => void;
}>;

export default function StepDetailContent({
   detail,
   contentOnly,
   showAllCode,
   onToggleAllCode,
   showAllOutput,
   onToggleAllOutput,
}: StepDetailContentProps) {
   const {t} = useTranslation("ask");
   const code = detail.code;
   const outputCode = detail.output;

   const codeLines = useMemo(() => (code ? keyedLines(code) : []), [code]);
   const hiddenCodeCount = Math.max(codeLines.length - CODE_LINE_CAP, 0);
   const visibleCode = showAllCode
      ? (code ?? "")
      : codeLines.slice(0, CODE_LINE_CAP).map(({line}) => line).join("\n");

   const outputLines = useMemo(() => (outputCode ? keyedLines(outputCode) : []), [outputCode]);
   const hiddenOutputCount = Math.max(outputLines.length - OUTPUT_LINE_CAP, 0);
   const visibleOutputLines = showAllOutput
      ? outputLines
      : outputLines.slice(0, OUTPUT_LINE_CAP);

   const languageLabel =
      detail.codeLanguage === "json" ? t("tools.labels.json") : detail.badgeText;

   return (
      <div className={cn("space-y-2 pb-2", !contentOnly && "pl-5 pr-1")}>
         {code && (
            <StepCodeBlock
               code={code}
               visibleCode={visibleCode}
               codeLanguage={detail.codeLanguage}
               languageLabel={languageLabel}
               lineCount={codeLines.length}
               expansion={{
                  hiddenCount: hiddenCodeCount,
                  showAll: showAllCode,
                  onToggleShowAll: onToggleAllCode,
               }}
            />
         )}
         {outputCode && (
            <StepOutput
               outputLabel={detail.outputLabel}
               failed={detail.failed}
               visibleLines={visibleOutputLines}
               expansion={{
                  hiddenCount: hiddenOutputCount,
                  showAll: showAllOutput,
                  onToggleShowAll: onToggleAllOutput,
               }}
            />
         )}
      </div>
   );
}
